import { CheckCircle } from "lucide-react";

import type { FooterCopy } from "@components/bluetooth-landing/Footer";
import type { HeroCopy } from "@components/bluetooth-landing/Hero";
import type { HubLinkListCopy } from "@components/bluetooth-landing/HubLinkList";
import type { NavbarCopy } from "@components/bluetooth-landing/Navbar";
import {
  contactFormPl,
  maintenanceClusterLinks,
  maintenanceKnowledgeBaseLinks,
  organizationStructuredData,
  serviceStructuredData,
} from "@data/maintenance-landing/shared";

export const title =
  "Utrzymanie i audyt oprogramowania - SLA, audyt kodu i modernizacja legacy";

export const description =
  "Utrzymanie aplikacji, audyt kodu, SLA i modernizacja legacy systemu. Polski software house dla zespołów z istniejącym produktem — opieka, diagnoza ryzyk i bezpieczna zmiana dostawcy.";

export const structuredData = [
  organizationStructuredData(description),
  serviceStructuredData(
    "Utrzymanie i audyt oprogramowania",
    description,
    "Application maintenance, code audit and legacy modernization",
  ),
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Usługi utrzymania i audytu oprogramowania",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Utrzymanie aplikacji",
        url: "https://bluesbrackets.com/utrzymanie-aplikacji",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Audyt kodu i modernizacja legacy",
        url: "https://bluesbrackets.com/audyt-kodu-i-modernizacja-legacy",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: "Przejęcie projektu od innego wykonawcy",
        url: "https://bluesbrackets.com/przejecie-projektu-od-innego-wykonawcy",
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Artykuły o utrzymaniu, audycie i zmianie dostawcy IT",
    itemListElement: maintenanceKnowledgeBaseLinks.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.text,
      url: `https://bluesbrackets.com${item.href}`,
    })),
  },
];

export const navbarCopy: NavbarCopy = {
  links: {
    targetAudience: "Dla kogo",
    values: "Baza wiedzy",
    services: "Usługi",
    process: "Proces",
    about: "O nas",
    faq: "FAQ",
  },
  cta: "Porozmawiajmy",
};

export const heroCopy: HeroCopy = {
  header: "Utrzymanie i audyt",
  headerTail: "oprogramowania",
  description:
    "Opieka nad istniejącym systemem — nie budowa od zera. SLA i monitoring, audyt kodu z planem modernizacji legacy oraz bezpieczne przejęcie projektu po innym wykonawcy.",
  cta: {
    text: "Darmowa konsultacja",
    href: "#kontakt",
  },
  secondaryCta: {
    text: "Zobacz usługi",
    href: "#uslugi",
  },
  benefits: [
    { text: "SLA i monitoring produkcji", Icon: CheckCircle },
    { text: "Audyt kodu i legacy", Icon: CheckCircle },
    { text: "Zmiana dostawcy bez chaosu", Icon: CheckCircle },
  ],
};

export const introCopy = {
  id: "dla-kogo",
  eyebrow: "Dla kogo",
  heading: "Gdy macie produkt na produkcji — i potrzebujecie opieki, nie kolejnego MVP",
  paragraphs: [
    "Ten klaster jest dla CTO, PM i founderów z działającą aplikacją lub platformą. Szukacie SLA i szybkiej reakcji na błędy, niezależnej oceny ryzyk przed dalszym rozwojem albo zmiany software house, bo poprzedni vendor nie dowozi tempa. To nie jest ścieżka „zbudujmy od zera” — to opieka i decyzje wokół tego, co już działa.",
    "Poniżej: utrzymanie aplikacji (mobile i web pod jednym modelem SLA), audyt kodu z modernizacją legacy oraz osobna ścieżka przejęcia projektu. Dobierzcie stronę do etapu — albo napiszcie, a wskażemy sensowny start.",
  ],
};

export const servicesListCopy: HubLinkListCopy = {
  id: "uslugi",
  eyebrow: "Usługi",
  heading: "Od czego chcecie zacząć?",
  intro:
    "Wybierzcie stronę najbliższą sytuacji — dyżur i monitoring, diagnoza ryzyk albo zmiana dostawcy IT.",
  items: [
    {
      href: "/utrzymanie-aplikacji",
      title:
        "Utrzymanie aplikacji - SLA, monitoring i szybkie reagowanie na błędy",
      description:
        "Opieka nad aplikacją mobilną lub webową: monitoring, czas reakcji i przewidywalny retainer — ten sam model niezależnie od platformy.",
    },
    {
      href: "/audyt-kodu-i-modernizacja-legacy",
      title:
        "Audyt kodu i modernizacja legacy systemów - ocena ryzyk przed dalszym rozwojem",
      description:
        "Audyt kodu, architektury i infrastruktury (np. AWS) oraz plan modernizacji — diagnoza, a potem opcjonalne wdrożenie zmian.",
    },
    {
      href: "/przejecie-projektu-od-innego-wykonawcy",
      title:
        "Przejęcie projektu od innego wykonawcy - bezpieczne wdrożenie i dalszy rozwój",
      description:
        "Zmiana dostawcy oprogramowania: handover dostępów, cutover odpowiedzialności, stabilizacja i powrót do tempa delivery.",
    },
  ],
};

export const blogListCopy: HubLinkListCopy = {
  id: "wartosci",
  eyebrow: "Baza wiedzy",
  heading: "Przeczytajcie przed rozmową",
  intro:
    "Widełki kosztów utrzymania i audytu oraz praktyczny przewodnik po zmianie dostawcy IT.",
  items: [
    {
      href: "/pl/blogs/ile-kosztuje-utrzymanie-lub-audyt-aplikacji",
      title: "Ile kosztuje utrzymanie lub audyt aplikacji? SLA i wycena audytu",
      description:
        "Orientacyjne koszty retainera SLA, monitoringu i audytu kodu — w jednym wpisie.",
    },
    {
      href: "/pl/blogs/jak-bezpiecznie-zmienic-dostawce-it",
      title: "Jak bezpiecznie zmienić dostawcę IT i przejąć istniejący projekt?",
      description:
        "Checklista dostępów, plan handoveru i cutover — bez szarej strefy odpowiedzialności na produkcji.",
    },
  ],
};

export const processCopy = {
  id: "proces",
  eyebrow: "Proces",
  heading: "Od rozmowy do przewidywalnej opieki",
  steps: [
    {
      title: "Rozmowa o systemie i celu",
      description:
        "Stack, historia awarii, czy chodzi o SLA, audyt, modernizację czy zmianę dostawcy. Dobieramy ścieżkę — nie wciskamy jednego pakietu wszystkim.",
    },
    {
      title: "Wejście: dostęp, przegląd, plan",
      description:
        "NDA, repo i środowiska, krótka mapa ryzyk. Potem umowa SLA, zakres audytu albo plan przejęcia z datą cutoveru.",
    },
    {
      title: "Opieka, raport i kolejne decyzje",
      description:
        "Monitoring i reakcja według SLA, raport po audycie albo stabilizacja po handouverze — a modernizację dokładamy tylko gdy ma sens biznesowy.",
    },
  ],
};

export const footerCopy: FooterCopy = {
  header: {
    h2: "Macie system na produkcji i potrzebujecie partnera do opieki?",
    p: "Umówcie rozmowę — opiszcie aplikację i sytuację, a wskażemy utrzymanie, audyt albo przejęcie projektu.",
  },
  cta: {
    href: "#kontakt",
    text: "Umów darmową konsultację",
    p: "Utrzymanie aplikacji, audyt kodu, SLA i modernizacja legacy. Polski software house — opieka nad istniejącym oprogramowaniem.",
    email: "hello@bluesbrackets.com",
    phone: "+48 535 462 678",
  },
  links: {
    h4: "Na skróty",
    links: [
      { href: "#uslugi", text: "Usługi" },
      { href: "#wartosci", text: "Baza wiedzy" },
      { href: "#proces", text: "Proces współpracy" },
      ...maintenanceClusterLinks.filter(
        l => l.href !== "/utrzymanie-i-audyt-oprogramowania",
      ),
    ],
  },
  knowledgeBase: {
    h4: "Baza wiedzy",
    links: [...maintenanceKnowledgeBaseLinks],
  },
  copyright: "bluesBrackets. Wszelkie prawa zastrzeżone.",
  privacyPolicy: "Polityka prywatności",
  privacyPolicyHref: "/policy",
  contactForm: contactFormPl,
};
